import { MinusOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { Button, Form, Input, Modal, Radio, Select, Space, Table, Tag, message } from "antd";
import axios from "axios";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { reminder } from "../../../../../helper/options";
import { serverUrl } from "../../../../../nestserver";
import { selectSchoolId } from "../../../../../redux/slices/auth.slice";

const FeeClass = ({ refreshKey = 0 }) => {
  const getCurrentAcademicYear = () => {
    const today = new Date();
    const curYear = today.getFullYear();
    const curMonth = today.getMonth() + 1;
    const startYear = curMonth >= 6 ? curYear : curYear - 1;
    const endYearShort = String(startYear + 1).slice(-2);
    return `${startYear}-${endYearShort}`;
  };

  const academicYears = [getCurrentAcademicYear()];
  const location = useLocation();
  const { Option } = Select;
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const schoolId = useSelector(selectSchoolId);
  const { data: schoolData } = useQuery(
    ["school-info-feeclass", schoolId],
    () =>
      axios
        .get(`${serverUrl}/school/${schoolId}`, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((r) => r.data?.result?.data),
    { enabled: !!schoolId }
  );
  const isManualMode = schoolData?.is_manual_fee_assignment_enabled === true;

  // Assign Fee modal state (manual assignment mode)
  const [assignModal, setAssignModal] = useState({ visible: false, student: null });
  const [assignTemplateId, setAssignTemplateId] = useState(null);
  const [assignCategoryName, setAssignCategoryName] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const handleAssignFee = async () => {
    if (!assignTemplateId || !assignModal.student) return;
    setAssigning(true);
    try {
      const selectedTemplate = (listdata || []).find((t) => t._id === assignTemplateId);
      const hasCategories = selectedTemplate?.categories?.length > 0;
      const body = hasCategories && assignCategoryName ? { categoryName: assignCategoryName } : {};
      await axios.patch(
        `${serverUrl}/admin/fee-template/${assignTemplateId}/assign-to-student/${assignModal.student._id}`,
        body,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        }
      );
      message.success("Fee template assigned successfully");
      setAssignModal({ visible: false, student: null });
      setAssignTemplateId(null);
      setAssignCategoryName(null);
      // Refresh student list
      if (classId && selectedYear) {
        const res = await axios.get(
          `${serverUrl}/admin/fee-template/class/${classId}`,
          {
            params: { feeTemplateId: feeTypeId, year: selectedYear },
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
            },
          }
        );
        setClassResponse(res.data?.result);
      }
    } catch (err) {
      message.error(
        err?.response?.data?.error?.message || "Failed to assign fee template"
      );
    } finally {
      setAssigning(false);
    }
  };

  const [sendReminder, setSendReminder] = useState(false);
  const [selectReminder, setSelectReminder] = useState();
  const [classResponse, setClassResponse] = useState();
  const [classId, setClassId] = useState();
  const [feeTypeId, setFeeTypeId] = useState();
  const [studentFee, setStudentFee] = useState();
  const searchInputRef = useRef(null);
  const [studentReminderIds, setStudentReminderIds] = useState([]);
  const [minRemainingAmount, setMinRemainingAmount] = useState(null);
  const [maxRemainingAmount, setMaxRemainingAmount] = useState(null);
  const [selectedYear, setSelectedYear] = useState(getCurrentAcademicYear());
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // In manual mode, fetch all enrolled students for the class so the table
  // is never empty — students without a StudentFee yet still show with the
  // "Assign Fee" button.
  const { data: classStudents } = useQuery(
    ["class-students-manual", classId],
    () =>
      axios
        .get(`${serverUrl}/students/class/${classId}`, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((r) => (Array.isArray(r.data) ? r.data : r.data?.data || [])),
    { enabled: !!classId && isManualMode }
  );

  const query = new URLSearchParams(location.search);
  const urlClassId = query.get("classId");
  const urlYear = query.get("year");
  const urlPage = query.get("page");

  useEffect(() => {
    if (urlClassId) setClassId(urlClassId);
    if (urlYear) setSelectedYear(urlYear);
    if (urlPage) setCurrentPage(Number(urlPage));
  }, [urlClassId, urlPage, urlYear]);

  //restore class and pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
    navigate({
      pathname: location.pathname,
      search: `?classId=${classId}&year=${selectedYear}&page=${page}`,
    }, {
      replace: true
    });
  };

  const [expandedRowKeys, setExpandedRowKeys] = useState([]); // Tracks expanded rows

  const toggleRowExpand = (record) => {
    const key = record?.student?._id;
    const isExpanded = expandedRowKeys.includes(key);
    setExpandedRowKeys((prevKeys) =>
      isExpanded ? prevKeys.filter((k) => k !== key) : [...prevKeys, key],
    );
  };

  // max-min filter
  const handleMinChange = (event) => {
    setMinRemainingAmount(event.target.value);
  };

  const handleMaxChange = (event) => {
    setMaxRemainingAmount(event.target.value);
  };

  async function fetchTemplateData(selectedYear, classId) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/admin/fee-template/list`,
      params: {
        year: selectedYear,
        classId: classId,
      },
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res?.data?.result;
  }
  const { data: listdata } = useQuery(
    ["fee-template", selectedYear, classId],
    () => fetchTemplateData(selectedYear, classId),
  );

  //handle class Select change
  const handleSelectChange = (classId) => {
    setClassId(classId);
    setFeeTypeId(null);
    setCurrentPage(1);

    navigate({
      pathname: location.pathname,
      search: `?classId=${classId}&year=${selectedYear}&page=1`,
    }, {
      replace: true
    });
  };

  useEffect(() => {
    if (!classId || !selectedYear) return;

    const fetchClassStudents = async () => {
      const res = await axios({
        method: "get",
        url: serverUrl + "/admin/fee-template/class/" + classId,
        params: {
          feeTemplateId: feeTypeId,
          year: selectedYear,
        },
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      return res.data;
    };

    fetchClassStudents().then((response) => {
      setClassResponse(response?.result);
    });
  }, [classId, feeTypeId, selectedYear, refreshKey]);

  //handleStudent
  const handleStudentFee = (studentId) => {
    const fetchStudentFee = async () => {
      const res = await axios({
        method: "get",
        url:
          serverUrl +
          "/admin/fee-template/class/" +
          classId +
          "/student/" +
          studentId,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      return res.data;
    };
    fetchStudentFee().then((response) => {
      setStudentFee(response?.result);
    });
  };

  const handleExport = async (e) => {
    e.preventDefault();
    if (!classId) return;
    try {
      const response = await axios({
        url: serverUrl + "/admin/fee-template/export",
        method: "GET",
        responseType: "blob",
        params: {
          classId,
          ...(selectedYear && { year: selectedYear }),
        },
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      const className =
        classes?.data?.find((c) => c._id === classId)?.name || "class";
      const yearLabel = selectedYear || new Date().getFullYear();
      saveAs(response.data, `FeeReport_class${className}_${yearLabel}.xlsx`);
    } catch (err) {
      message.error("Export failed. Please try again.");
    }
  };

  // fetch classes
  async function fetchClasses() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });

    return res.data;
  }
  const classes = useQuery("classes", fetchClasses);
  // Table Settings

  // Search Filter

  const [, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const getColumnSearchProps = (dataIndex, label) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Search ${label || dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={
              <SearchOutlined
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            }
            size="small"
            style={{ width: 90, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: "#4382DF" }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.select(), 100);
      }
    },
    render: (text) => (searchedColumn === dataIndex ? text : text),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getStudentFeeBreakdown = (record) => {
    const studentFees = record?.student?.studentFee || [];
    const tuitionRecord = studentFees.find(
      (sf) => sf?.feeTemplate?.type?.toLowerCase() === "tution"
    );
    const otherFeeInst = tuitionRecord?.installment_list?.find(
      (inst) => inst.name?.toLowerCase() === "other fee"
    );
    const otherTotal = otherFeeInst
      ? Number(otherFeeInst.payable_components?.[0]?.total_amount || otherFeeInst.payable_components?.total_amount || 0)
      : 0;

    const miscDiscount = otherFeeInst?.discount || 0;

    // Sum tuition installment gross amounts (payable_components[0].total_amount) as the
    // source of truth — sf.total is stale when add-misc-fee updates an installment
    // without recalculating sf.total on the backend.
    const allInstallments = tuitionRecord?.installment_list || [];
    const tuitionInstGross = allInstallments.reduce((sum, inst) => {
      const pc = Array.isArray(inst.payable_components)
        ? inst.payable_components[0]
        : inst.payable_components || {};
      return sum + Number(pc?.total_amount || 0);
    }, 0);

    const tuitionGross = !tuitionRecord
      ? 0
      : tuitionInstGross > 0
        ? tuitionInstGross
        : Number(record?.total || 0) + Number(record?.discount || 0) + miscDiscount - Number(record?.additional || 0);

    // Add transportation / accommodation / other StudentFee records so that
    // Total Fee in the main row matches the combined Outstanding from the backend.
    const nonTuitionGross = studentFees
      .filter((sf) => sf?.feeTemplate?.type?.toLowerCase() !== "tution")
      .reduce((sum, sf) => {
        const sfInsts = sf?.installment_list || [];
        const sfInstGross = sfInsts.reduce((s, inst) => {
          const pc = Array.isArray(inst.payable_components)
            ? inst.payable_components[0]
            : inst.payable_components || {};
          return s + Number(pc?.total_amount || 0);
        }, 0);
        return sum + (sfInstGross > 0 ? sfInstGross : Number(sf?.total || 0) + Number(sf?.discount || 0));
      }, 0);

    const originalTotal = tuitionGross + nonTuitionGross;

    const tuitionTotal = tuitionGross - otherTotal;

    return {
      tuitionTotal,
      otherTotal,
      originalTotal,
    };
  };

  const getExpandedRowData = (studentFees) => {
    if (!studentFees || studentFees.length === 0) return [];

    const displayRows = [];
    studentFees.forEach((sf) => {
      const sfDiscount = Number(sf?.discount || 0);
      if (sf?.feeTemplate?.type?.toLowerCase() === "tution") {
        const otherFeeInst = sf?.installment_list?.find(
          (inst) => inst.name?.toLowerCase() === "other fee",
        );
        const otherTotal = otherFeeInst
          ? Number(
              otherFeeInst.payable_components?.[0]?.total_amount ||
                otherFeeInst.payable_components?.total_amount ||
                0,
            )
          : 0;

        if (otherTotal > 0) {
          const otherPaid = otherFeeInst?.paid || 0;
          const otherRemaining = otherFeeInst?.remaining || 0;

          const tuitionInsts = (sf.installment_list || []).filter(
            (inst) => inst.name?.toLowerCase() !== "other fee",
          );
          const grossTuition =
            tuitionInsts.length > 0
              ? tuitionInsts.reduce((sum, inst) => {
                  const pc = Array.isArray(inst.payable_components)
                    ? inst.payable_components[0]
                    : inst.payable_components || {};
                  return sum + Number(pc?.total_amount || 0);
                }, 0)
              : Math.max(0, (sf.total || 0) + sfDiscount + (otherFeeInst?.discount || 0) - otherTotal);

          // Tuition row
          displayRows.push({
            ...sf,
            _id: `${sf._id}-tuition`,
            feeTemplate: {
              ...sf.feeTemplate,
              type: "Tuition",
            },
            total: grossTuition,
            fullAmount: grossTuition,
            discount: sfDiscount,
            paid: (sf.paid || 0) - otherPaid,
            remaining: (sf.remaining || 0) - otherRemaining,
          });

          displayRows.push({
            ...sf,
            _id: `${sf._id}-other`,
            feeTemplate: {
              ...sf.feeTemplate,
              type: "Miscellaneous Fees",
            },
            total: otherTotal - (otherFeeInst?.discount || 0),
            fullAmount: otherTotal,
            discount: otherFeeInst?.discount || 0,
            paid: otherPaid,
            remaining: otherRemaining,
            due_date: otherFeeInst?.due_date || sf?.due_date,
            last_paid: otherFeeInst?.last_paid || sf?.last_paid,
          });

          return;
        }
      }

      displayRows.push({
        ...sf,
        fullAmount: Number(sf?.total || 0) + sfDiscount,
        discount: sfDiscount,
      });
    });

    return displayRows;
  };

  const columns = [
    {
      title: "Name",
      key: "",
      align: "center",
      ...getColumnSearchProps(["first_name", "last_name"], "Name"),
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center capitalize"
        >
          {record?.student?.first_name + " " + record?.student?.last_name}
          {record?.student?.studentFee?.find((sf) => sf?.feeTemplate?.type === "tution")?.fee_category && (
            <Tag style={{ marginLeft: 6, fontSize: 11, verticalAlign: "middle" }}>
              {record.student.studentFee.find((sf) => sf?.feeTemplate?.type === "tution").fee_category}
            </Tag>
          )}
        </div>
      ),
      onFilter: (value, record) =>
        (
          record?.student?.first_name?.toLowerCase() +
          " " +
          record?.student?.last_name?.toLowerCase()
        ).includes(value.toLowerCase()),
    },

    {
      title: "Total Fee",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => {
        const total = feeTypeId
          ? Number(record?.total || 0) + Number(record?.discount || 0)
          : getStudentFeeBreakdown(record).originalTotal;
        return (
          <div
            key={record?._id}
            style={{ color: "#111111" }}
            className="text-base text-center"
          >
            {total}
          </div>
        );
      },
    },
    {
      title: "Discount",
      dataIndex: [],
      key: "discount",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#ff4d4f" }}
          className="text-base text-center font-semibold"
        >
          {record?.discount || 0}
        </div>
      ),
    },
    {
      title: "Paid Amount",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div style={{ color: "#616161" }} className=" text-base text-center">
          {record?.paid}
        </div>
      ),
    },
    {
      title: "Outstanding",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div style={{ color: "#616161" }} className=" text-base text-center">
          {record?.remaining}
        </div>
      ),
    },
    {
      title: "Last Paid",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center capitalize"
        >
          {record?.last_paid
            ? moment(record?.last_paid).format("Do MMM YYYY")
            : ""}
        </div>
      ),
    },
    {
      title: "Reminder",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center capitalize"
        >
          {record?.reminder_sent
            ? moment(record?.reminder_sent).format("DD MMM YYYY hh:mm:ss")
            : ""}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => {
        const isAssigned = !record?._unassigned;
        return (
        <div className="flex flex-col space-y-1 justify-between items-center">
          {/* View — only when fee is assigned */}
          {(!isManualMode || isAssigned) && (
            <div
              key={record?._id}
              className="text-base font-semibold text-center text-blue-500 cursor-pointer"
              onClick={() => {
                handleStudentFee(record?.student?._id);
                navigate({
                  pathname: `/dashboard/admin/menu/class/${classId}/student/${record?.student?._id}`,
                  search: `?classId=${classId}&year=${selectedYear}&page=${currentPage || 1}`,
                });
              }}
            >
              View
            </div>
          )}

          {/* Assign Fee — only when in manual mode and not yet assigned */}
          {isManualMode && !isAssigned && (
            <Button
              size="small"
              type="primary"
              ghost
              onClick={(e) => {
                e.stopPropagation();
                setAssignTemplateId(null);
                setAssignModal({ visible: true, student: record?.student });
              }}
            >
              Assign Fee
            </Button>
          )}

          {/* Expand row — only when fee is assigned */}
          {(!isManualMode || isAssigned) && (
          <div
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleStudentFee(record?.student?._id);
              toggleRowExpand(record);
            }}
          >
            {expandedRowKeys.includes(record?.student?._id) ? (
              <MinusOutlined />
            ) : (
              <PlusOutlined />
            )}
          </div>
          )}
        </div>
        );
      },
    },
  ];
  const feeColumn = [
    {
      title: "Name",
      key: "",
      align: "center",
      render: (record) => {
        const type = record?.feeTemplate?.type;
        const displayType =
          type?.toLowerCase() === "tution"
            ? "Tuition"
            : type?.toLowerCase() === "other fee"
            ? "Miscellaneous Fees"
            : type;
        return (
          <div
            key={record?._id}
            style={{ color: "#111111" }}
            className=" text-base font-semibold text-center capitalize"
          >
            {displayType}
          </div>
        );
      },
    },

    {
      title: "Total Amount",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record}
          style={{ color: "#111111" }}
          className="text-base text-center"
        >
          {record?.fullAmount ?? record?.total}
        </div>
      ),
    },
    {
      title: "Discount",
      dataIndex: [],
      key: "discount",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#ff4d4f" }}
          className="text-base text-center font-semibold"
        >
          {record?.discount || 0}
        </div>
      ),
    },
    {
      title: "Paid Amount",
      dataIndex: [],
      key: "",
      render: (record) => (
        <div style={{ color: "#616161" }} className=" text-base">
          {record?.paid}
        </div>
      ),
    },
    {
      title: "Outstanding",
      dataIndex: [],
      key: "",
      render: (record) => (
        <div style={{ color: "#616161" }} className=" text-base">
          {record?.remaining}
        </div>
      ),
    },
    {
      title: "Due Date",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center capitalize"
        >
          {moment(record?.due_date).format("Do MMM YYYY")}
        </div>
      ),
    },
    {
      title: "Last Paid",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center capitalize"
        >
          {record?.last_paid
            ? moment(record?.last_paid).format("Do MMM YYYY")
            : ""}
        </div>
      ),
    },
    {
      title: "Reminder",
      dataIndex: [],
      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center capitalize"
        >
          {record?.reminder_sent
            ? moment(record?.reminder_sent).format("DD MMM YYYY hh:mm:ss")
            : ""}
        </div>
      ),
    },
  ];
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      selectedRows &&
        selectedRows.length > 0 &&
        setStudentReminderIds(selectedRows.map((item) => item?.student?._id));
    },
    getCheckboxProps: (record) => ({}),
  };

  function onFinish(values) {
    let payLoad;

    if (values.reminder === reminder.now) {
      payLoad = {
        now: true,
        daily: false,
        before_due_date: false,
        after_due_date: false,
        till_date: null,
        studentsId: studentReminderIds ? studentReminderIds : null,
      };
    } else if (values.reminder === reminder.daily) {
      payLoad = {
        now: false,
        daily: true,
        before_due_date: false,
        after_due_date: false,
        till_date: values?.tillDate
          ? moment(values?.tillDate).format("YYYY-MM-DD")
          : null,
        studentsId: studentReminderIds ? studentReminderIds : null,
      };
    } else if (
      selectReminder === reminder.dueDate &&
      values?.dueDateOption === reminder.beforeDate
    ) {
      payLoad = {
        now: false,
        daily: false,
        before_due_date: true,
        after_due_date: false,
        till_date: null,
        studentsId: studentReminderIds ? studentReminderIds : null,
      };
    } else if (
      selectReminder === reminder.dueDate &&
      values?.dueDateOption === reminder.afterDate
    ) {
      payLoad = {
        now: false,
        daily: false,
        before_due_date: false,
        after_due_date: true,
        till_date: null,
        studentsId: studentReminderIds ? studentReminderIds : null,
      };
    }

    if (classId && payLoad && studentReminderIds.length > 0) {
      axios
        .post(
          serverUrl + "/admin/fee-template/class/" + classId + "/reminder/",
          payLoad,
          {
            headers: {
              Authorization: `Bearer ${window.localStorage.getItem(
                "jwt-token",
              )}`,
            },
          },
        )
        .then((response) => {
          const result = response?.data?.result;
          if (result?.success === false) {
            message.error(result?.message || "Reminder could not be sent");
          } else {
            message.success(result?.message || "Reminder Sent");
          }
          setSendReminder(false);

          // Only reflect "sent" in the table when at least something actually
          // went out — result.success is false when the SMS wallet balance
          // couldn't cover any of this batch.
          if (result?.success !== false) {
            const updatedClassResponse = classResponse?.map((student) => {
              if (studentReminderIds.includes(student?.student?._id)) {
                return {
                  ...student,
                  reminder_sent: new Date().toISOString(),
                };
              }
              return student;
            });
            setClassResponse(updatedClassResponse);
          }
          form.resetFields();
        })
        .catch((error) => {
          message.error(error?.response?.data?.error?.errors);
        });
    }
  }

  // In manual mode, merge all enrolled students with any existing StudentFee
  // records so students without fees still appear and can be assigned.
  const effectiveData = React.useMemo(() => {
    if (!isManualMode || !classStudents) return classResponse;
    return classStudents.map((student) => {
      const existing = (classResponse || []).find(
        (r) => r?.student?._id === student._id
      );
      return (
        existing || {
          _id: student._id,
          student,
          total: 0,
          paid: 0,
          remaining: 0,
          discount: 0,
          _unassigned: true,
        }
      );
    });
  }, [isManualMode, classStudents, classResponse]);

  useEffect(() => {
    const source = effectiveData;
    const filtered = source?.filter((record) => {
      const remaining = record?.remaining || 0;
      return (
        (minRemainingAmount === null || remaining >= minRemainingAmount) &&
        (maxRemainingAmount === null || remaining <= maxRemainingAmount)
      );
    });
    setFilteredData(filtered);
    if (minRemainingAmount === "" || maxRemainingAmount === "") {
      setFilteredData(source);
    }
  }, [minRemainingAmount, maxRemainingAmount, effectiveData]);

  const handleStopReminder = () => {
    const payload = {
      studentId: studentReminderIds.length > 0 ? studentReminderIds : [],
    };
    if (studentReminderIds.length > 0) {
      axios
        .post(serverUrl + "/admin/fee-template/stop-reminder/", payload, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((response) => {
          message.success(response?.data?.result?.message) ||
            message.success("Reminder stopped");
          const updatedClassResponse = classResponse.map((student) => {
            if (studentReminderIds.includes(student?.student?._id)) {
              return {
                ...student,
                reminder_sent: new Date().toISOString(),
              };
            }
            return student;
          });
          setClassResponse(updatedClassResponse);
        })
        .catch((error) => {
          message.error(error?.response?.data?.error?.errors);
        });
    }
  };

  return (
    <>
      <div className="mt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full "
        >
          <div className="text-2xl text-orange-500 font-semibold pb-4">
            Student Fee
          </div>

          {/* Class tabs */}
          <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            Select Class
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
            {classes?.isSuccess &&
              classes?.data?.map((cls) => {
                const isActive = classId === cls._id;
                return (
                  <div
                    key={cls._id}
                    onClick={() => handleSelectChange(cls._id)}
                    style={{
                      padding: "6px 18px",
                      borderRadius: 20,
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      userSelect: "none",
                      transition: "all 0.15s",
                      background: isActive ? "#f97316" : "#f1f5f9",
                      color: isActive ? "#fff" : "#64748b",
                      border: `1.5px solid ${isActive ? "#f97316" : "#e2e8f0"}`,
                      boxShadow: isActive ? "0 2px 8px rgba(249,115,22,0.25)" : "none",
                    }}
                  >
                    {cls.name}
                  </div>
                );
              })}
          </div>

          {/* Year + Fee Type filters */}
          <div className="flex space-x-3">
            <div className="flex flex-col w-310">
              <h3 className="text-base pb-3">Year*</h3>
              <Select
                value={selectedYear}
                onChange={(val) => setSelectedYear(val)}
                className="w-full bg-gray-300"
                style={{ border: "6px", backgroundColor: "#EBEBEB" }}
              >
                {academicYears.map((yr) => (
                  <Option key={yr} value={yr}>
                    {yr}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col w-310">
              <h3 className="text-base pb-3">Fee Type</h3>
              <Select
                showSearch
                showArrow
                placeholder="Select fee type"
                className="w-full bg-gray-300 capitalize"
                style={{ border: "6px", backgroundColor: "#EBEBEB" }}
                value={feeTypeId}
                getPopupContainer={(trigger) => trigger.parentNode}
                onChange={(value) => setFeeTypeId(value)}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                disabled={!classId || !selectedYear}
              >
                {listdata
                  ?.filter((data) => data?.type)
                  .map((data) => (
                    <Option key={data._id} value={data._id}>
                      {data?.type}
                    </Option>
                  ))}
                <Option key="static-transportation" value="transportation">
                  Transportation
                </Option>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4 pt-6">
              <div>
                <Input
                  placeholder="Minimum"
                  value={minRemainingAmount}
                  onChange={handleMinChange}
                  className="w-100px"
                />
              </div>
              <div>&gt;= Outstanding &lt;=</div>
              <div>
                <Input
                  placeholder="Maximum"
                  value={maxRemainingAmount}
                  onChange={handleMaxChange}
                  className="w-100px"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end items-center pt-4 gap-6">
            <div className=" flex items-center gap-3">
              <h5 className="text-base pb-2"> Reminder</h5>
              <Button onClick={() => setSendReminder(true)}>Send</Button>
              <Button onClick={handleStopReminder}>Stop</Button>
            </div>
            <Button
              size="large"
              type="primary"
              htmlType="download"
              className="text-white w-9"
              onClick={(e) => handleExport(e)}
            >
              Export
            </Button>
          </div>
          {/* ── Class-wide summary strip ── computed from ALL records, not the current page ── */}
          {filteredData && filteredData.length > 0 && (() => {
            let stripTotalFee = 0;
            let stripDiscount = 0;
            let stripPaid = 0;
            let stripOutstanding = 0;
            filteredData.forEach((record) => {
              stripTotalFee += feeTypeId
                ? Number(record?.total || 0) + Number(record?.discount || 0)
                : getStudentFeeBreakdown(record).originalTotal;
              stripDiscount += Number(record?.discount || 0);
              stripPaid += Number(record?.paid || 0);
              stripOutstanding += Number(record?.remaining || 0);
            });
            const isFiltered =
              (minRemainingAmount !== null && minRemainingAmount !== "") ||
              (maxRemainingAmount !== null && maxRemainingAmount !== "");

            const cardBase = {
              borderRadius: 10,
              padding: "18px 22px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            };
            const labelStyle = {
              fontSize: 12,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              color: "#64748b",
            };
            const valueStyle = (color) => ({
              fontSize: 22,
              fontWeight: 700,
              color,
              lineHeight: 1.2,
            });

            return (
              <div style={{ marginBottom: 24, marginTop: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 12, letterSpacing: "0.02em" }}>
                  Class Fee Summary
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
                  <div style={{ ...cardBase, background: "#f0f9ff", border: "1px solid #bae6fd" }}>
                    <div style={labelStyle}>Students</div>
                    <div style={valueStyle("#0369a1")}>{filteredData.length}</div>
                  </div>
                  <div style={{ ...cardBase, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <div style={labelStyle}>Total Fee</div>
                    <div style={valueStyle("#1e293b")}>₹{stripTotalFee.toLocaleString("en-IN")}</div>
                  </div>
                  <div style={{ ...cardBase, background: "#fef2f2", border: "1px solid #fecaca" }}>
                    <div style={labelStyle}>Discount</div>
                    <div style={valueStyle("#dc2626")}>₹{stripDiscount.toLocaleString("en-IN")}</div>
                  </div>
                  <div style={{ ...cardBase, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                    <div style={labelStyle}>Collected</div>
                    <div style={valueStyle("#15803d")}>₹{stripPaid.toLocaleString("en-IN")}</div>
                  </div>
                  <div style={{ ...cardBase, background: "#fff7ed", border: "1px solid #fed7aa" }}>
                    <div style={labelStyle}>Outstanding</div>
                    <div style={valueStyle("#c2410c")}>₹{stripOutstanding.toLocaleString("en-IN")}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "right", marginTop: 8 }}>
                  {isFiltered
                    ? `Showing ${filteredData.length} of ${classResponse?.length || 0} students (outstanding filter active)`
                    : `All ${filteredData.length} students in class`}
                </div>
              </div>
            );
          })()}
          <div className="py-5">
            <Table
              rowSelection={{
                type: "checkbox",
                ...rowSelection,
              }}
              rowKey={(record) => record?.student?._id}
              columns={columns}
              dataSource={
                filteredData && filteredData.length > 0 ? filteredData : []
              }
              pagination={{
                current: currentPage,
                onChange: handlePageChange,
              }}
              loading={classResponse?.isLoading}
              expandable={{
                expandedRowKeys,
                expandIcon: () => null,
                onExpandedRowsChange: (expandedKeys) =>
                  setExpandedRowKeys(expandedKeys),
                expandedRowRender: (record) => {
                  return (
                    <div className="p-4">
                      <Table
                        rowKey="_id"
                        columns={feeColumn}
                        dataSource={
                          record && record?.student?.studentFee?.length > 0
                            ? getExpandedRowData(record?.student?.studentFee)
                            : []
                        }
                        pagination={false}
                        loading={studentFee?.isLoading}
                      />
                    </div>
                  );
                },
              }}
            />
          </div>
        </div>
      </div>
      {/* ---------------------------------- remindermodal------------------------------------ */}

      <Modal
        width={400}
        visible={sendReminder}
        onCancel={() => {
          setSendReminder(false);
        }}
        footer={null}
      >
        <div>
          <Form
            form={form}
            layout="vertical"
            name="basic"
            onFinish={onFinish}
            autoComplete="off"
          >
            <h3 className="text-base pb-3 pt-4">Reminder</h3>
            <Form.Item name="reminder" className="input-font-weight">
              <Radio.Group
                value={selectReminder}
                onChange={(e) => {
                  setSelectReminder(e.target.value);
                  if (e.target.value !== "dueDate") {
                    form.setFieldsValue({
                      tillDate: undefined,
                      beforeDate: undefined,
                      afterDate: undefined,
                    });
                  }
                  if (e.target.value === "dueDate") {
                    form.setFieldsValue({
                      tillDate: undefined,
                    });
                    form.resetFields(["tillDate", "beforeDate", "afterDate"]);
                  }
                  if (e.target.value === "daily" || e.target.value === "now") {
                    form.setFieldsValue({
                      dueDateOption: undefined,
                    });
                  }
                }}
                className="space-y-4"
                label="selectReminder"
              >
                <div className="flex flex-col">
                  <Radio value="now">Now</Radio>
                  <div className="pt-3 pb-3">
                    <Radio value="daily">Daily</Radio>
                    <Form.Item
                      name="tillDate"
                      noStyle
                      rules={[
                        {
                          required: selectReminder === "daily",
                          message: "Please select till date",
                        },
                      ]}
                    >
                      <DatePicker
                        format={"DD-MM-YYYY"}
                        placeholder="Till Date"
                        style={{ marginLeft: "30px" }}
                        getPopupContainer={(trigger) => trigger.parentNode}
                        disabled={selectReminder !== "daily"}
                        disabledDate={(current) => {
                          return current && current < moment().startOf("day");
                        }}
                      />
                    </Form.Item>
                  </div>

                  <div className="flex  items-center">
                    <Radio value="dueDate">Due Date</Radio>{" "}
                    <div>
                      {selectReminder === reminder.dueDate && (
                        <Form.Item
                          name="dueDateOption"
                          noStyle
                          rules={[
                            {
                              validator: (_, value) => {
                                if (
                                  value !== "beforeDate" &&
                                  value !== "afterDate"
                                ) {
                                  return Promise.reject(
                                    "Please select either before date or after date",
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Select
                            getPopupContainer={(trigger) => trigger.parentNode}
                            placeholder="Select mode"
                          >
                            <Select.Option value="beforeDate">
                              Before Date
                            </Select.Option>
                            <Select.Option value="afterDate">
                              After Date
                            </Select.Option>
                          </Select>
                        </Form.Item>
                      )}
                    </div>
                  </div>
                </div>
              </Radio.Group>
            </Form.Item>
            <div className="flex justify-center items-center py-5">
              <div className="inline-flex items-center space-x-2">
                <Button
                  size="large"
                  type="primary"
                  htmlType="submit"
                  className="text-white"
                >
                  Send
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Assign Fee Modal — manual fee assignment mode */}
      <Modal
        title={`Assign Fee — ${assignModal.student?.first_name || ""} ${assignModal.student?.last_name || ""}`}
        open={assignModal.visible}
        onCancel={() => {
          setAssignModal({ visible: false, student: null });
          setAssignTemplateId(null);
          setAssignCategoryName(null);
        }}
        onOk={handleAssignFee}
        okText="Assign"
        confirmLoading={assigning}
        okButtonProps={{
          disabled: (() => {
            if (!assignTemplateId) return true;
            const t = (listdata || []).find((x) => x._id === assignTemplateId);
            if (t?.categories?.length > 0 && !assignCategoryName) return true;
            return false;
          })(),
        }}
      >
        <div className="mb-4 text-sm text-gray-500">
          Select the fee template to assign to this student.
        </div>
        <Select
          style={{ width: "100%" }}
          placeholder="Select fee template"
          value={assignTemplateId}
          onChange={(val) => {
            setAssignTemplateId(val);
            setAssignCategoryName(null);
          }}
        >
          {(listdata || [])
            .filter((t) => t.status === "applied")
            .map((t) => (
              <Select.Option key={t._id} value={t._id}>
                {t.name} — ₹{t.total || (t.fee_components || []).reduce((s, c) => s + Number(c.amount || 0), 0)}
              </Select.Option>
            ))}
        </Select>
        {(() => {
          const selectedTemplate = (listdata || []).find((t) => t._id === assignTemplateId);
          if (!selectedTemplate?.categories?.length) return null;
          return (
            <div className="mt-3">
              <div className="text-sm text-gray-500 mb-1">Select fee category</div>
              <Select
                style={{ width: "100%" }}
                placeholder="Select category"
                value={assignCategoryName}
                onChange={setAssignCategoryName}
                options={selectedTemplate.categories.map((c) => ({
                  label: `${c.name} — ₹${Number(c.tuition_amount).toLocaleString("en-IN")}`,
                  value: c.name,
                }))}
              />
            </div>
          );
        })()}
      </Modal>
    </>
  );
};

export default FeeClass;
